/**
 * /invites/accept?token=... — accept a workspace invite. Cycle 17.
 *
 * For an authenticated user whose email matches the invite, this calls
 * /invites/accept and redirects to the dashboard. For users whose email
 * doesn't match, the API returns a 403 with a clear message which we
 * render verbatim.
 *
 * Anonymous users land here directly from the email link — the _app
 * layout will bounce them to /login first; after login they come back
 * here and the accept call lands.
 */
import { useEffect, useState } from "react";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";
import { api } from "../../../lib/api.js";

const search = z.object({
  token: z.string().min(20).max(200).optional(),
});

interface InvitePreview {
  email: string;
  role: string;
  expiresAt: string;
  workspaceName: string;
}

export const Route = createFileRoute("/_app/invites/accept")({
  validateSearch: search.parse,
  component: AcceptPage,
});

function AcceptPage() {
  const { token } = useSearch({ from: "/_app/invites/accept" });
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This link is missing the invite token. Use the original email link.");
      return;
    }
    api
      .get<{ invite: InvitePreview }>(`/invites/lookup?token=${encodeURIComponent(token)}`)
      .then((r) => setPreview(r.data.invite))
      .catch((err) => {
        const msg = (err as { response?: { data?: { message?: string } } }).response
          ?.data?.message;
        setError(msg ?? "Failed to look up invite");
      });
  }, [token]);

  async function accept() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ workspaceId: string; role: string }>(
        "/invites/accept",
        { token },
      );
      // Switch active workspace to the one we just joined so the next
      // navigate lands the user inside it.
      await api.post("/me/active-workspace", {
        workspaceId: r.data.workspaceId,
      });
      setDone(true);
      setTimeout(() => void navigate({ to: "/dashboard" }), 800);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to accept invite");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={{ maxWidth: 520, margin: "40px auto", padding: 24 }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Workspace invite</h1>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 12, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {done ? (
        <div className="brut-card" style={{ background: "#defbe5", padding: 12 }}>
          Joined! Redirecting to your dashboard…
        </div>
      ) : preview ? (
        <div className="brut-card" style={{ padding: 14, display: "grid", gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>You're invited to</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{preview.workspaceName}</div>
          </div>
          <div style={{ fontSize: 13 }}>
            Role: <strong>{preview.role}</strong>
          </div>
          <div style={{ fontSize: 13 }}>
            Sent to: <code>{preview.email}</code>
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>
            Expires {new Date(preview.expiresAt).toLocaleDateString()}. You must
            be logged in as <code>{preview.email}</code> to accept — if you're
            logged in as a different account, log out and log back in with the
            invited email.
          </div>
          <button
            onClick={() => void accept()}
            disabled={busy}
            className="brut-button"
          >
            {busy ? "Joining…" : "Accept and join"}
          </button>
        </div>
      ) : !error ? (
        <p>Loading invite…</p>
      ) : null}
    </main>
  );
}
